import React from 'react';
import { buildClassNames } from '@frontend/jetlend-web-ui/src/utils/classNameUtils';
import Link from 'next/link';
import SectionPart from '@app/ui/SectionPart/SectionPart';
import Image from '@app/components/Image/Image';
import HtmlMarkup from '@app/components/HtmlMarkup/HtmlMarkup';
import CourseHeaderBadge from '@app/features/academy/course/CourseHeader/CourseHeaderBadge/CourseHeaderBadge';
import {
    CourseComplexity,
    IAchievementItemApiModel,
} from '@app/models/features/course';
import { getDescriptionWithEnding } from '@app/utils/getDescriptionWithEnding';
import AchievementHeaderBadge from '@app/features/academy/course/CourseHeader/CourseHeaderBadge/AchievementHeaderBadge';
import NotPassedCardFooter from '@app/features/academy/courses/CoursesItemCard/CardFooter/NotPassedCardFooter';
import PassedCardFooter from '@app/features/academy/courses/CoursesItemCard/CardFooter/PassedCardFooter';
import ActiveCourseCardFooter from '@app/features/academy/courses/CoursesItemCard/CardFooter/ActiveCourseCardFooter';

import styles from './CoursesItemCard.module.scss';

interface IProps {
    /**
     * Описание курса
     */
    description: string;
    /**
     * Ссылка на страницу курса
     */
    url: string;
    /**
     * Картинка курса
     */
    image: string;
    /**
     * Alt тег картинки курса
     */
    imageAlt?: string;
    /**
     * Сложность курса
     */
    complexity: CourseComplexity;
    /**
     * Кол-во блоков в курсе
     */
    blocksCount: number;
    /**
     * Пройденной кол-во блоков
     */
    passedBlocksCount: number;
    /**
     * Прогресс курса в минутах
     */
    estimateTime: number;
    /**
     * Курс пройден/не пройден
     */
    passed: boolean;
    /**
     * Достижение получаемое в данном курсе
     */
    reward?: IAchievementItemApiModel;
    /**
     * Иконка навыка
     */
    videoEstimatedTime?: number;
    /**
     * Иконка навыка
     */
    contentEstimatedTime?: number;
    /**
     * Белый фон карточки
     */
    white?: boolean;
}

/**
 * Карточка курса
 */
const CoursesItemCard: React.FC<IProps> = props => {
    const {
        description,
        blocksCount,
        complexity,
        estimateTime,
        image,
        imageAlt,
        passed,
        passedBlocksCount,
        url,
        reward,
        white,
        videoEstimatedTime,
        contentEstimatedTime,
    } = props;

    const passedProgress = blocksCount <= 0 ? 0 : (passedBlocksCount ?? 0)/blocksCount;
    const notPassedCourse = !passed && (!passedBlocksCount || passedBlocksCount === 0 );

    const cardClassNames = buildClassNames(styles, [
        'card',
        white && 'card--white',
    ]);

    return (
        <Link href={url} className={styles['card-link']}>
            <SectionPart
                className={cardClassNames}
            >
                <div className={styles['card__image-wrapper']}>
                    <Image
                        src={image}
                        alt={imageAlt ?? ''}
                        fill
                        className={styles['card__image']}
                    />
                </div>
                <div className={styles['badges']}>
                    <CourseHeaderBadge
                        type="complexity"
                        value={complexity}
                    />
                    {reward && (
                        <AchievementHeaderBadge
                            value="+1 награда"
                            achievementIcon={reward.icon}
                            achievementName={reward.title}
                        />
                    )}
                </div>
                <div className={styles['card__text']}>
                    <HtmlMarkup
                        tag="div"
                        className={styles['card__text-description']}
                        markup={getDescriptionWithEnding(description)}
                    />
                </div>
                <div>
                    {passed && <PassedCardFooter />}
                    {!passed && passedBlocksCount > 0 && (
                        <ActiveCourseCardFooter
                            estimateTime={estimateTime}
                            passedBlocksCount={passedBlocksCount}
                            passedProgress={passedProgress}
                        />
                    )}
                    {notPassedCourse && (
                        <NotPassedCardFooter
                            videoEstimatedTime={videoEstimatedTime}
                            contentEstimatedTime={contentEstimatedTime}
                            blocksCount={blocksCount}
                            url={url}
                        />
                    )}
                </div>
            </SectionPart>
        </Link>
    );
};

export default CoursesItemCard;